import React, { useState } from "react";
import RequirementFull from "../dtos/RequirementFullDTO";
import ConfirmEmailAlert from 'features/requirements/components/requirementAlerts/ConfirmEmailAlert';
import ConfirmMobileAlert from 'features/requirements/components/requirementAlerts/ConfirmMobileAlert';
import RequirementAlerts from 'features/requirements/components/requirementAlerts/RequirementAlerts';
import RequiredDocumentsAlert from 'features/requirements/components/requirementAlerts/RequiredDocumentsAlert';
import { useQuery } from "data/hooks/useQuery";
import DocumentTypeEnumDTO from "features/shared/enums/dynamic/DocumentTypeEnumDTO";
import { Requirements_MobilePhoneVerification, Requirements_RequirementDocumentsType } from "data/endpoints";
import DataQuery from "data/DataQuery";
import { getRouteParams } from "features/shared/helpers/routeParams";
import ConfirmMobileDialog from 'features/requirements/components/confirmMobileDialog/ConfirmMobileDialog';
import PhoneNumberVerificationDTO from "../dtos/PhoneNumberVerificationDTO";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IRepository from "data/IRepository";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { trackPromise } from "react-promise-tracker";
import { RequirementDetail_ConfirmMobileDialog } from "features/shared/components/loaders/promiseLoadingAreas";
import PostResponse from "data/PostResponse";
import { Nullable } from "features/shared/types/nullable";
import { useTranslation } from "react-i18next";

interface Props {
  requirement: RequirementFull;
  setRequirement: React.Dispatch<React.SetStateAction<RequirementFull>>;
}

const RequirementAlertsSection = ({ requirement, setRequirement }: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const verificationRepo = useInjection<IRepository<PhoneNumberVerificationDTO>>(IocTypes.Repository);

  // state
  const [confirmMobileDialogShow, setConfirmMobileDialogShow] = useState(false);
  const [phoneVerificationPostResponse, setPhoneVerificationPostResponse] = useState<Nullable<PostResponse<string>>>(null);

  // fetch required documents
  const documentsQuery: DataQuery = { url: Requirements_RequirementDocumentsType, routeParams: getRouteParams("requirementId", requirement.requirementId!) }
  const { data: requiredDocuments } = useQuery<DocumentTypeEnumDTO[]>(documentsQuery, [], [], {useTracker: false});

  // helpers
  const getUpdatedRequirement = (): RequirementFull => {
    let newRequirement: RequirementFull = {...requirement};
    newRequirement.actualState!.code = "ZMENA_UDAJU_PROVEDENA";
    newRequirement.actualState!.name = tLabels("requirementAlertSection.dataChangeMade");
    newRequirement.resolveDate = new Date();

    return newRequirement;
  }

  // handlers
  const phoneNumberVerificationSubmitHandler = async (phoneVerification: PhoneNumberVerificationDTO) => {
    let response = await trackPromise(
      verificationRepo.post({
        url: Requirements_MobilePhoneVerification,
        data: phoneVerification,
        routeParams: getRouteParams("requirementId", requirement.requirementId!)
      }),
      RequirementDetail_ConfirmMobileDialog
    );

    if (response.status < 300) {  
      if (phoneVerification.descriptorCode == "TMOVEROVACIKOZADEJ") {
        setRequirement(getUpdatedRequirement());
        setConfirmMobileDialogShow(false);
      } else {
        setPhoneVerificationPostResponse({status: response.status, data: tLabels("requirementAlertSection.phoneVerificationMessage")});
      } 
    } else {
      let errorMessage = response.status == 400 ? tLabels("requirementAlertSection.phoneVerificationWarningMessage") : tLabels("requirementAlertSection.phoneVerificationErrorMessage");
      setPhoneVerificationPostResponse({status: response.status, data: errorMessage});
    }
  }

  // render
  return (
    <>
      <RequirementAlerts>
        <ConfirmEmailAlert requirement={requirement} />
        <ConfirmMobileAlert requirement={requirement} setConfirmMobileDialogShow={setConfirmMobileDialogShow} />
        <RequiredDocumentsAlert requiredDocuments={requiredDocuments} />
      </RequirementAlerts>
      <ConfirmMobileDialog
        show={confirmMobileDialogShow}
        setShow={setConfirmMobileDialogShow}
        onSubmit={phoneNumberVerificationSubmitHandler}
        postResponse={phoneVerificationPostResponse}
      />
    </>
  )
};

export default RequirementAlertsSection;
